import { useCallback, useState } from "react";

export type LicenseDetailsDisplayMode = "pane" | "fullscreen";

const LICENSE_DETAILS_DISPLAY_MODE_KEY = "license_details_display_mode";

function readStoredDisplayMode(): LicenseDetailsDisplayMode {
  try {
    return window.localStorage.getItem(LICENSE_DETAILS_DISPLAY_MODE_KEY) ===
      "fullscreen"
      ? "fullscreen"
      : "pane";
  } catch {
    return "pane";
  }
}

/**
 * @param initialMode Overrides the stored preference for this mount only, so a
 *   deep link can force a mode without rewriting the user's saved preference.
 */
export function useLicenseDetailsDisplayMode(
  initialMode?: LicenseDetailsDisplayMode,
) {
  const [displayMode, setDisplayModeState] =
    useState<LicenseDetailsDisplayMode>(
      () => initialMode ?? readStoredDisplayMode(),
    );

  const setDisplayMode = useCallback((mode: LicenseDetailsDisplayMode) => {
    setDisplayModeState(mode);
    try {
      window.localStorage.setItem(LICENSE_DETAILS_DISPLAY_MODE_KEY, mode);
    } catch {
      // Storage may be unavailable; keep the preference for this mount.
    }
  }, []);

  const toggleDisplayMode = useCallback(() => {
    setDisplayModeState((current) => {
      const next = current === "pane" ? "fullscreen" : "pane";
      try {
        window.localStorage.setItem(LICENSE_DETAILS_DISPLAY_MODE_KEY, next);
      } catch {
        // Storage may be unavailable; keep the preference for this mount.
      }
      return next;
    });
  }, []);

  return { displayMode, setDisplayMode, toggleDisplayMode };
}
